import { useQuery } from "@tanstack/react-query";
import { api } from "../../api/client";
import type { Me } from "../../api/types";
import { S } from "../../styles";
import { hasRole } from "../../auth/roleHelpers";
import { RecommendationCard } from "./RecommendationCard";
import {
  LoadingState,
  ErrorState,
  EmptyState,
} from "../../components/QueryStates";

export function RecommendationsPanel({ me }: { me: Me | null }) {
  const canAction = hasRole(me, "buyer", "planner");
  // Accepting a traceability shortfall is an airworthiness decision, not a
  // buying one — the API enforces the same split.
  const canOverrideTrace = hasRole(me, "manager", "admin");

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["recommendations"],
    queryFn: () => api.listRecommendations(0, 50),
    refetchInterval: 10_000,
  });
  const recs = data?.items ?? [];

  if (isLoading) return <LoadingState label="Loading recommendations…" />;
  if (error) return <ErrorState error={error} />;

  const pending = recs.filter((r) => r.status === "pending_review");
  const actioned = recs.filter((r) => r.status !== "pending_review");

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <h2 style={{ ...S.h2, margin: 0 }}>
          🤖 AI Recommendations{" "}
          {pending.length > 0 && (
            <span
              style={{
                fontSize: 13,
                background: "#e67700",
                color: "#fff",
                borderRadius: 10,
                padding: "1px 7px",
              }}
            >
              {pending.length} pending
            </span>
          )}
        </h2>
        <button style={S.btnSm} onClick={() => void refetch()}>
          ↺ Refresh
        </button>
      </div>

      {!canAction && (
        <div
          style={{
            ...S.card,
            background: "#fffbf0",
            border: "1px solid #ffe066",
            marginBottom: 16,
          }}
        >
          <p style={{ ...S.muted, margin: 0 }}>
            ℹ Your role can view recommendations but cannot approve or reject
            them. Approval requires the <strong>buyer</strong> or{" "}
            <strong>planner</strong> role.
          </p>
        </div>
      )}

      {recs.length === 0 ? (
        <EmptyState message="No recommendations yet. Submit a requirement to trigger the AI agent pipeline." />
      ) : (
        <>
          {pending.length > 0 && (
            <div>
              <h3 style={{ ...S.h3, color: "#e67700" }}>
                Pending Review ({pending.length})
              </h3>
              {pending.map((r) => (
                <RecommendationCard
                  key={r.id}
                  rec={r}
                  canAction={canAction}
                  onAction={() => void refetch()}
                  awardLocked={r.rfq_award_locked}
                  canOverrideTrace={canOverrideTrace}
                />
              ))}
            </div>
          )}
          {actioned.length > 0 && (
            <div>
              <h3 style={{ ...S.h3, color: "#888" }}>
                History ({actioned.length})
              </h3>
              {actioned.map((r) => (
                <RecommendationCard
                  key={r.id}
                  rec={r}
                  canAction={false}
                  onAction={() => void refetch()}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
